import { z } from 'zod'

/**
 * Translation of the articles in a report, on demand.
 *
 * The text a translation produces is stored as ordinary item OVERRIDES — the same slot a hand
 * edit uses — which is why undo, reset, the edit trail and every renderer need no new code to
 * understand it. What the translator actually cost us is kept separately, in a content-addressed
 * cache, so reversing a translation never throws away the thing we paid for.
 *
 * Nothing here runs during a pull. Translation is only ever an action someone takes.
 */

/**
 * The languages the picker offers, mirroring what the admin portal's translation modal has
 * offered IAs for years. The code is DeepL's own target code, so nothing has to be mapped on the
 * way out — the legacy portal keeps two translation tables for exactly that reason.
 */
export const TRANSLATION_LANGUAGES = [
  { code: 'EN-US', name: 'English (US)' },
  { code: 'EN-GB', name: 'English (UK)' },
  { code: 'AR', name: 'Arabic' },
  { code: 'BG', name: 'Bulgarian' },
  { code: 'CS', name: 'Czech' },
  { code: 'DA', name: 'Danish' },
  { code: 'DE', name: 'German' },
  { code: 'EL', name: 'Greek' },
  { code: 'ES', name: 'Spanish' },
  { code: 'ET', name: 'Estonian' },
  { code: 'FI', name: 'Finnish' },
  { code: 'FR', name: 'French' },
  { code: 'HU', name: 'Hungarian' },
  { code: 'ID', name: 'Indonesian' },
  { code: 'IT', name: 'Italian' },
  { code: 'JA', name: 'Japanese' },
  { code: 'KO', name: 'Korean' },
  { code: 'LT', name: 'Lithuanian' },
  { code: 'LV', name: 'Latvian' },
  { code: 'NB', name: 'Norwegian (Bokmål)' },
  { code: 'NL', name: 'Dutch' },
  { code: 'PL', name: 'Polish' },
  { code: 'PT-PT', name: 'Portuguese (European)' },
  { code: 'PT-BR', name: 'Portuguese (Brazilian)' },
  { code: 'RO', name: 'Romanian' },
  { code: 'RU', name: 'Russian' },
  { code: 'SK', name: 'Slovak' },
  { code: 'SL', name: 'Slovenian' },
  { code: 'SV', name: 'Swedish' },
  { code: 'TR', name: 'Turkish' },
  { code: 'UK', name: 'Ukrainian' },
  { code: 'ZH', name: 'Chinese (simplified)' },
] as const satisfies ReadonlyArray<{ code: string; name: string }>

export type TranslationLanguage = (typeof TRANSLATION_LANGUAGES)[number]['code']

const LANGUAGE_CODES = TRANSLATION_LANGUAGES.map((language) => language.code)

export const translationLanguageSchema = z.enum(
  LANGUAGE_CODES as unknown as [TranslationLanguage, ...TranslationLanguage[]],
)

export function translationLanguageName(code: string): string {
  const exact = TRANSLATION_LANGUAGES.find((language) => language.code === code)
  if (exact) return exact.name
  // DeepL answers with base codes — 'EN', 'PT' — where our targets are regional. Name the language
  // without the region rather than showing the reader a code.
  const base = TRANSLATION_LANGUAGES.find(
    (language) => language.code.split('-')[0] === code.toUpperCase(),
  )
  return base ? withoutTrailingParenthetical(base.name) : code
}

/**
 * `name.replace(/\s*\(.*\)$/, '')` as a scan (CLI-876): drops a trailing `(…)` on the last line,
 * from its first `(`, with the whitespace before it — 'English (British)' reads 'English'.
 */
function withoutTrailingParenthetical(name: string): string {
  if (!name.endsWith(')')) return name
  const inner = name.slice(0, -1)
  // `.` stops at a line terminator, so the `(` must sit after the last one.
  let lineStart = 0
  for (let i = 0; i < inner.length; i += 1) {
    if ('\n\r\u2028\u2029'.includes(inner[i]!)) lineStart = i + 1
  }
  const open = inner.indexOf('(', lineStart)
  if (open === -1) return name
  let start = open
  while (start > 0 && /\s/.test(name[start - 1]!)) start -= 1
  return name.slice(0, start)
}

/**
 * Whether an article already reads in the target language, so the modal can offer "only the ones
 * that need it". The snapshot's language is the collector's ISO code ('es', 'pt-BR'); the target
 * is DeepL's ('ES', 'PT-BR'). Compare on the base tag only — an article tagged `en` should not be
 * translated into `EN-GB`, and `pt` should count as already-Portuguese for `PT-BR`.
 *
 * Unknown language means "translate it": a missing tag is the collector's silence, not a claim.
 */
export function alreadyInLanguage(snapshotLanguage: string | null, target: string): boolean {
  if (!snapshotLanguage) return false
  const base = (value: string) => value.split('-')[0]?.toLowerCase() ?? ''
  return base(snapshotLanguage) === base(target)
}

/**
 * The fields a translation may write. Full text is deliberately absent: it is the warehouse's
 * licensed copy, read on request and never stored here, so it is translated for reading in the
 * editor and nowhere else. See `routes/public-full-text.ts` and CLI-712's deferred section.
 */
export const translatableFieldSchema = z.enum(['headline', 'summary', 'snippets'])
export type TranslatableField = z.infer<typeof translatableFieldSchema>

export const TRANSLATABLE_FIELDS: ReadonlyArray<{ key: TranslatableField; label: string }> = [
  { key: 'headline', label: 'Headlines' },
  { key: 'snippets', label: 'Snippets' },
  { key: 'summary', label: 'Summaries' },
]

/**
 * Where a translation lands. One article, or one header's list of one source — the same two units
 * the editor already works in. There is deliberately no whole-report scope: a header is as much as
 * one click should be able to spend.
 */
export const translationScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('item'), itemId: z.uuid() }),
  z.object({ kind: z.literal('section'), sectionId: z.uuid() }),
])
export type TranslationScope = z.infer<typeof translationScopeSchema>

export const translateRequestSchema = z.object({
  scope: translationScopeSchema,
  targetLang: translationLanguageSchema,
  fields: z.array(translatableFieldSchema).min(1),
  /** Leave alone the articles already written in the target language. */
  skipSameLanguage: z.boolean().default(true),
})
export type TranslateRequest = z.infer<typeof translateRequestSchema>

/**
 * What a translation would cost before anyone commits to it: how many articles are in scope, how
 * many of them the cache already answers for free, and the characters we would actually send.
 * The modal shows this because DeepL bills per character and a header can be a hundred articles.
 */
export const translatePreflightSchema = z.object({
  articles: z.number().int().nonnegative(),
  /** Articles wholly answerable from the cache — reused at no cost. */
  reusable: z.number().int().nonnegative(),
  /** Characters that would leave the building. Cache hits excluded. */
  characters: z.number().int().nonnegative(),
})
export type TranslatePreflight = z.infer<typeof translatePreflightSchema>

export const translateResponseSchema = z.object({
  /** Articles whose text changed. */
  translated: z.number().int().nonnegative(),
  /** Of those, how many were served entirely from the cache. */
  reused: z.number().int().nonnegative(),
  characters: z.number().int().nonnegative(),
})
export type TranslateResponse = z.infer<typeof translateResponseSchema>

/**
 * A translated full text, for reading in the expanded card. Never stored and never sent: the
 * hosted full-text page is unauthenticated, so translating on request there would be an unmetered
 * spend anyone with the link could pull.
 */
export const translateFullTextResponseSchema = z.object({
  /** What DeepL detected the original to be, when it said. */
  sourceLang: z.string().nullable(),
  content: z.string(),
})
export type TranslateFullTextResponse = z.infer<typeof translateFullTextResponseSchema>
